import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    Typography,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Stack,
    LinearProgress,
} from "@mui/material";
import { AiOutlineRead, AiFillBulb, AiOutlineHistory } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import {
    javaQuestionBank,
    shuffle,
    getBadge,
    saveScore,
    loadScores,
} from "./JavaQuizData";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const ExamCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const OptionButton = styled(Button)(({ theme }) => ({
    margin: "8px 0",
    textTransform: "none",
    width: "100%",
    fontSize: "1rem",
    borderRadius: "8px",
    padding: "12px",
    justifyContent: "flex-start",
    textAlign: "left",
    transition: "0.3s",
    "&:hover": {
        transform: "translateX(5px)",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function JavaCertificationExam() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Logic: Shuffle questions and options on load
    const [questions] = useState(() =>
        shuffle(
            javaQuestionBank.map((q) => ({
                ...q,
                options: shuffle(q.options),
            }))
        )
    );

    const [current, setCurrent] = useState(0);
    const [score, setScore] = useState(0);
    const [selected, setSelected] = useState(null);
    const [showAnswer, setShowAnswer] = useState(false);
    const [userAnswers, setUserAnswers] = useState([]);
    const [timer, setTimer] = useState(1200); // 20 minutes
    const [finished, setFinished] = useState(false);
    const [showScroll, setShowScroll] = useState(false);

    // Timer Logic
    useEffect(() => {
        if (!finished && timer > 0) {
            const id = setTimeout(() => setTimer((t) => t - 1), 1000);
            return () => clearTimeout(id);
        } else if (timer === 0 && !finished) {
            setFinished(true);
        }
    }, [timer, finished]);

    // Scroll Logic
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setUserAnswers((prev) => [...prev, opt]);
        setShowAnswer(true);
        if (opt === questions[current].answer) setScore((s) => s + 1);
    };

    const next = () => {
        if (current + 1 < questions.length) {
            setCurrent((c) => c + 1);
            setShowAnswer(false);
            setSelected(null);
        } else {
            setFinished(true);
            const badge = getBadge(score, questions.length);
            saveScore({
                date: new Date().toLocaleString(),
                score,
                timeRemaining: timer,
                badge,
            });
        }
    };

    const categoryStats = questions.reduce((acc, q, i) => {
        if (!acc[q.category]) acc[q.category] = { total: 0, correct: 0 };
        acc[q.category].total++;
        if (finished && userAnswers[i] === q.answer) acc[q.category].correct++;
        return acc;
    }, {});

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVA CERTIFICATION EXAM
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    {!finished ? (
                        <>
                            {/* ✅ Progress and Timer UI */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6" fontWeight={700} color="goldenrod">
                                    Question {current + 1} of {questions.length}
                                </Typography>
                                <Typography variant="h6" fontWeight={700} sx={{ color: timer < 60 ? "error.main" : "white" }}>
                                    ⏱ {formatTime(timer)}
                                </Typography>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={((current + 1) / questions.length) * 100}
                                sx={{ mb: 4, height: 8, borderRadius: 5, bgcolor: "rgba(255,255,255,0.1)", "& .MuiLinearProgress-bar": { bgcolor: "goldenrod" } }}
                            />

                            <ExamCard>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    {questions[current].question}
                                </Typography>



                                <Grid container spacing={2}>
                                    {questions[current].options.map((opt, i) => (
                                        <Grid item xs={12} key={i}>
                                            <OptionButton
                                                variant={showAnswer && opt === questions[current].answer ? "contained" : "outlined"}
                                                sx={{
                                                    borderColor: "rgba(255,255,255,0.3)",
                                                    color: "white",
                                                    bgcolor: showAnswer && opt === questions[current].answer ? "success.dark" : "transparent",
                                                    "&.MuiButton-containedSuccess": { bgcolor: "success.main" }
                                                }}
                                                color={showAnswer ? (opt === questions[current].answer ? "success" : opt === selected ? "error" : "primary") : "primary"}
                                                onClick={() => handleAnswer(opt)}
                                            >
                                                {i + 1}. {opt}
                                            </OptionButton>
                                        </Grid>
                                    ))}
                                </Grid>

                                {showAnswer && (
                                    <Zoom in={showAnswer}>
                                        <Box sx={{ mt: 4, p: 3, bgcolor: "rgba(255,255,255,0.05)", borderRadius: "10px", borderLeft: "4px solid goldenrod" }}>
                                            <Typography variant="subtitle1" fontWeight={800} color="goldenrod" gutterBottom>
                                                EXPLANATION
                                            </Typography>
                                            <Typography variant="body1">
                                                {questions[current].explanation}
                                            </Typography>
                                            <ActionButton onClick={next} sx={{ mt: 3 }}>
                                                {current + 1 < questions.length ? "Next Question" : "Finish Exam"}
                                            </ActionButton>
                                        </Box>
                                    </Zoom>
                                )}
                            </ExamCard>
                        </>
                    ) : (
                        /* ✅ Results View */
                        <Zoom in={finished}>
                            <Box>
                                <ExamCard sx={{ textAlign: "center", py: 6 }}>
                                    <Typography variant="h3" fontWeight={800} color="goldenrod" gutterBottom>
                                        EXAM COMPLETE
                                    </Typography>
                                    <Typography variant="h4" mb={2}>
                                        Final Score: {score} / {questions.length}
                                    </Typography>
                                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                                        <Avatar sx={{ bgcolor: "goldenrod", width: 60, height: 60 }}>
                                            <AiFillBulb size={35} color="#100720" />
                                        </Avatar>
                                        <Typography variant="h5" fontWeight={700}>
                                            Badge: {getBadge(score, questions.length)}
                                        </Typography>
                                    </Stack>
                                </ExamCard>

                                <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3} textAlign="center">
                                    PERFORMANCE BY CATEGORY
                                </Typography>

                                <Grid container spacing={3}>
                                    {Object.entries(categoryStats).map(([cat, data]) => (
                                        <Grid item xs={12} md={4} key={cat}>
                                            <ExamCard sx={{ height: "100%", textAlign: "center" }}>
                                                <Typography variant="h6" fontWeight={800} gutterBottom>{cat}</Typography>
                                                <Typography variant="h4" color="goldenrod">
                                                    {Math.round((data.correct / data.total) * 100)}%
                                                </Typography>
                                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                    {data.correct} out of {data.total} correct
                                                </Typography>
                                            </ExamCard>
                                        </Grid>
                                    ))}
                                </Grid>

                                <Typography variant="h5" fontWeight={800} color="goldenrod" mt={6} mb={3} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <AiOutlineHistory /> RECENT ATTEMPTS
                                </Typography>

                                {loadScores().map((entry, idx) => (
                                    <ExamCard key={idx}>
                                        <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                                            <Typography fontWeight={700}>{entry.date}</Typography>
                                            <Typography color="goldenrod" fontWeight={800}>SCORE: {entry.score}</Typography>
                                            <Typography sx={{ opacity: 0.7 }}>Badge: {entry.badge}</Typography>
                                        </Stack>
                                    </ExamCard>
                                ))}

                                <Box sx={{ textAlign: "center", mt: 6 }}>
                                    <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                                        <ActionButton variant="contained">BACK TO DASHBOARD</ActionButton>
                                    </Link>
                                </Box>
                            </Box>
                        </Zoom>
                    )}
                </Grid>
            </Grid>

            {/* ✅ Floating Scroll FAB */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
